import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Tag, Percent, DollarSign, Sparkles, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getSessionToken = () => localStorage.getItem('session_token');

// Convert local datetime to PST for display
const toPSTString = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", { 
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};

export default function Promotions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    enabled: false,
    name: "",
    discount_type: "percent",
    discount_value: "",
    description: "",
    expires_at: ""
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/settings/promotion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        enabled: response.data.enabled || false,
        name: response.data.name || "",
        discount_type: response.data.discount_type || "percent",
        discount_value: response.data.discount_value || "",
        description: response.data.description || "",
        expires_at: response.data.expires_at || ""
      });
    } catch (error) {
      console.error("Failed to fetch promotion settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (settings.enabled && !settings.name.trim()) {
      toast.error("Please enter a promotion name");
      return;
    }
    if (settings.enabled && (!settings.discount_value || settings.discount_value <= 0)) {
      toast.error("Please enter a valid discount value");
      return;
    }

    setSaving(true);
    try {
      const token = getSessionToken();
      await axios.put(`${API}/settings/promotion`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promotion settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Preview the discount
  const previewDiscount = () => {
    const val = parseFloat(settings.discount_value) || 0;
    if (settings.discount_type === "percent") {
      return `${val}% off`;
    }
    return `$${val.toFixed(2)} off`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/settings')}
              className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-600" />
            </button>
            <div>
              <h1 className="font-barlow text-2xl font-bold text-zinc-950 uppercase">Specials & Promotions</h1>
              <p className="text-sm text-zinc-500">Configure discounts for instant estimates</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Enable Toggle */}
        <div className="bg-white rounded-xl border border-zinc-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">Enable Promotion</h3>
                <p className="text-sm text-zinc-500">Show special offer on instant estimates</p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              data-testid="promotion-toggle"
            />
          </div>
        </div>

        {/* Promotion Details */}
        {settings.enabled && (
          <div className="bg-white rounded-xl border border-zinc-200 p-6 space-y-5">
            {/* Promotion Name */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Promotion Name *
              </label>
              <input
                type="text"
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                placeholder="e.g., Spring Special, Neighborhood Discount"
                className="w-full px-4 py-3 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#207024]/20 focus:border-[#207024]"
                data-testid="promotion-name-input"
              />
            </div>

            {/* Discount Type */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Discount Type
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setSettings({ ...settings, discount_type: "percent" })}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium flex items-center justify-center gap-2 transition-all ${
                    settings.discount_type === "percent"
                      ? "border-[#207024] bg-[#207024]/5 text-[#207024]"
                      : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
                  }`}
                  data-testid="discount-type-percent"
                >
                  <Percent className="w-5 h-5" />
                  Percentage
                </button>
                <button
                  onClick={() => setSettings({ ...settings, discount_type: "fixed" })}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium flex items-center justify-center gap-2 transition-all ${
                    settings.discount_type === "fixed"
                      ? "border-[#207024] bg-[#207024]/5 text-[#207024]"
                      : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
                  }`}
                  data-testid="discount-type-fixed"
                >
                  <DollarSign className="w-5 h-5" />
                  Fixed Amount
                </button>
              </div>
            </div>

            {/* Discount Value */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Discount Value *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">
                  {settings.discount_type === "percent" ? "%" : "$"}
                </span>
                <input
                  type="number"
                  min="0"
                  step={settings.discount_type === "percent" ? "1" : "0.01"}
                  value={settings.discount_value}
                  onChange={(e) => setSettings({ ...settings, discount_value: e.target.value })}
                  placeholder={settings.discount_type === "percent" ? "10" : "25.00"}
                  className="w-full pl-10 pr-4 py-3 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#207024]/20 focus:border-[#207024]"
                  data-testid="discount-value-input"
                />
              </div>
              {settings.discount_value > 0 && (
                <p className="text-sm text-[#207024] mt-2">
                  Customers will see: <strong>{previewDiscount()}</strong>
                </p>
              )}
            </div>

            {/* Description (Optional) */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Description <span className="text-zinc-400">(optional)</span>
              </label>
              <input
                type="text"
                value={settings.description}
                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                placeholder="e.g., Limited time savings when you book today"
                className="w-full px-4 py-3 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#207024]/20 focus:border-[#207024]"
                data-testid="promotion-description-input"
              />
            </div>

            {/* Expiration Date */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Expiration Date <span className="text-zinc-400">(optional)</span>
              </label>
              <input
                type="datetime-local"
                value={settings.expires_at ? settings.expires_at.slice(0, 16) : ""}
                onChange={(e) => setSettings({ ...settings, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#207024]/20 focus:border-[#207024]"
                data-testid="promotion-expires-input"
              />
              <p className="text-xs text-zinc-500 mt-1.5">
                Promotion will auto-disable after this date. Leave empty for no expiration.
                {settings.expires_at && (
                  <span className="block mt-1 text-amber-600">
                    Expires: {toPSTString(settings.expires_at)} PST
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Preview */}
        {settings.enabled && settings.name && settings.discount_value > 0 && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-5 h-5" />
              <span className="text-sm font-medium opacity-90">Preview - How customers will see it</span>
            </div>
            <h3 className="text-xl font-bold mb-1">{settings.name}</h3>
            <p className="text-lg">
              Save {settings.discount_type === "percent" ? `${settings.discount_value}%` : `$${parseFloat(settings.discount_value).toFixed(2)}`} on your service
            </p>
            {settings.description && (
              <p className="text-sm opacity-90 mt-2">{settings.description}</p>
            )}
            {settings.expires_at && (
              <p className="text-sm opacity-80 mt-2 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Ends {toPSTString(settings.expires_at)} PST
              </p>
            )}
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-4 bg-[#207024] text-white font-bold rounded-xl hover:bg-[#185a1c] disabled:opacity-50 transition-colors"
          data-testid="save-promotion-button"
        >
          {saving ? "Saving..." : "Save Promotion Settings"}
        </button>
      </div>
    </div>
  );
}
